/**
 * Custom Lexical transformers for Slack's mrkdwn format
 * Slack uses different syntax than standard Markdown:
 * - *text* for bold (not **text**)
 * - _text_ for italic (not *text*)
 * - ~text~ for strikethrough (not ~~text~~)
 *
 * IMPORTANT: We do NOT spread the standard TRANSFORMERS here because it includes
 * ITALIC_STAR (tag: "*", format: italic) which directly conflicts with SLACK_BOLD
 * (tag: "*", format: bold), causing both markers to be stripped during the
 * import → export round-trip through the Lexical editor.
 * Instead, we explicitly include only the non-conflicting element transformers.
 */

import {
  CODE,
  HEADING,
  INLINE_CODE,
  ORDERED_LIST,
  QUOTE,
  UNORDERED_LIST,
  type TextFormatTransformer,
  type TextMatchTransformer,
  type Transformer,
} from "@lexical/markdown";
import { $createTextNode, TextNode } from "lexical";
import emojiMap from "./emojiMap";

// Converts Slack :emoji_name: codes to unicode characters for display in the
// editor. Slack accepts native unicode when publishing, so the round-trip is
// safe. Returning null from export lets the default text serialization output
// the unicode character as-is.
export const EMOJI: TextMatchTransformer = {
  dependencies: [TextNode],
  export: () => null,
  importRegExp: /:[\w+-]+:/,
  regExp: /:[\w+-]+:/,
  replace: (textNode, match) => {
    const code = match[0].slice(1, -1); // strip surrounding colons
    const char = emojiMap[code];
    const node = $createTextNode(char ?? match[0]); // fallback to :code: if unknown
    textNode.replace(node);
  },
  trigger: ":",
  type: "text-match",
};

// Slack Bold: *text* (single asterisk)
export const SLACK_BOLD: TextFormatTransformer = {
  format: ["bold"],
  tag: "*",
  type: "text-format",
};

// Slack Italic: _text_ (single underscore)
export const SLACK_ITALIC: TextFormatTransformer = {
  format: ["italic"],
  tag: "_",
  type: "text-format",
};

// Slack Strikethrough: ~text~ (single tilde)
export const SLACK_STRIKETHROUGH: TextFormatTransformer = {
  format: ["strikethrough"],
  tag: "~",
  type: "text-format",
};

// Combine all Slack transformers.
// EMOJI must come first to prevent underscores in emoji names from being treated as italic.
// Slack text-format transformers come before any structural ones so they win tag conflicts.
// Non-conflicting element transformers (code, lists, quotes, headings) come last.
export const SLACK_TRANSFORMERS: Array<Transformer> = [
  EMOJI,
  SLACK_BOLD,
  SLACK_ITALIC,
  SLACK_STRIKETHROUGH,
  INLINE_CODE,
  CODE,
  QUOTE,
  ORDERED_LIST,
  UNORDERED_LIST,
  HEADING,
];
